import { ColumnDef } from '@tanstack/react-table';
import { Badge } from 'components/ui/badge';
import { DataTableColumnHeader } from '../../../../components/blocks/data-table/data-table-column-header';
import { IamData } from '../../services/user-service';
import { DataTableRowActions } from './iam-table-row-actions';
import { Copy } from 'lucide-react';

type StatusOption = {
  value: string | boolean;
  label: string;
  color: 'success' | 'error';
};

const getUserStatuses = (t: (key: string) => string): StatusOption[] => [
  { value: 'active', label: t('ACTIVE'), color: 'success' },
  { value: 'inactive', label: t('INACTIVE'), color: 'error' },
];

interface ColumnFactoryProps {
  onViewDetails: (user: IamData) => void;
  onResetPassword: (user: IamData) => void;
  onResendActivation?: (user: IamData) => void;
  onDeactivateUser?: (user: IamData) => void;
  onChangePlan?: (user: IamData) => void;
  onCancelSubscription?: (user: IamData) => void;
  onCopyEmail?: (email: string) => void;
  t: (key: string) => string;
}

export const createIamTableColumns = ({
  onViewDetails,
  onResetPassword,
  onResendActivation,
  onDeactivateUser,
  onChangePlan,
  onCancelSubscription,
  onCopyEmail,
  t
}: ColumnFactoryProps): ColumnDef<IamData, any>[] => [
    {
      id: 'fullName',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('NAME')} />,
      accessorFn: (row) => {
        const fullName = `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim();
        return fullName || '-';
      },
      cell: ({ row }) => {
        const fullName = `${row.original.firstName ?? ''} ${row.original.lastName ?? ''}`.trim();
        return (
          <div className="flex items-center">
            <span className="max-w-[300px] truncate font-medium">{fullName || '-'}</span>
          </div>
        );
      },
    },
    {
      id: 'email',
      accessorFn: (row) => row.email,
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMAIL')} />,
      cell: ({ row }) => (
        <div className="flex items-center gap-2 group">
          <span className="max-w-[300px] truncate">{row.original.email}</span>
          {onCopyEmail && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onCopyEmail(row.original.email);
              }}
              className="flex items-center justify-center h-6 w-6 p-1 rounded hover:bg-muted transition-colors opacity-0 group-hover:opacity-100"
              title={t('COPY_EMAIL')}
            >
              <Copy size={12} className="text-muted-foreground hover:text-foreground" />
            </button>
          )}
        </div>
      ),
    },
    {
      id: 'createdDate',
      accessorFn: (row) => row.createdDate,
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('JOINED_ON')} />,
      cell: ({ row }) => {
        const date = new Date(row.original.createdDate);
        return (
          <div className="flex items-center">
            <span>
              {new Date(date)
                .toLocaleDateString('de-CH', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
                .split('/')
                .join('/')}
            </span>
          </div>
        );
      },
      sortingFn: (rowA, rowB) => {
        const a = new Date(rowA.original.createdDate).getTime();
        const b = new Date(rowB.original.createdDate).getTime();
        return a < b ? -1 : a > b ? 1 : 0;
      },
      filterFn: (row, id, value) => {
        if (!value?.from || !value?.to) return true;
        const rowDate = new Date(row.getValue(id));
        return rowDate >= value.from && rowDate <= value.to;
      },
    },
    {
      id: 'lastLoggedInTime',
      accessorFn: (row) => row.lastLoggedInTime,
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('LAST_LOGIN')} />,
      cell: ({ row }) => {
        const date = new Date(row.original.lastLoggedInTime);
        if (date.getFullYear() === 1) {
          return <div className="text-muted-foreground">-</div>;
        }
        return (
          <div>
            <span>
              {new Date(date)
                .toLocaleDateString('de-CH', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
                .split('/')
                .join('/')}
            </span>
          </div>
        );
      },
      sortingFn: (rowA, rowB) => {
        const a = new Date(rowA.original.lastLoggedInTime).getTime();
        const b = new Date(rowB.original.lastLoggedInTime).getTime();
        return a < b ? -1 : a > b ? 1 : 0;
      },
      filterFn: (row, id, value) => {
        if (!value?.from || !value?.to) return true;
        const rowDate = new Date(row.getValue(id));
        return rowDate >= value.from && rowDate <= value.to;
      },
    },
    {
      id: 'active',
      accessorFn: (row) => row.active,
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
      cell: ({ row }) => {
        const status = getUserStatuses(t).find(
          (status) => status.value === (row.original.active ? 'active' : 'inactive')
        );
        if (!status) return null;
        return (
          <div className="flex items-center">
            <Badge
              variant="outline"
              className={status.color === 'success' ? 'text-success' : 'text-error'}
            >
              {status.label}
            </Badge>
          </div>
        );
      },
      sortingFn: (rowA, rowB, columnId) => {
        const a = rowA.getValue(columnId);
        const b = rowB.getValue(columnId);

        if (b === a) return 0;
        return a ? -1 : 1;
      },
      filterFn: (row, id, value: string[]) => {
        if (value.length === 0) return true;
        const cellValue = row.getValue(id);
        return value.includes(String(cellValue));
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DataTableRowActions
          row={row}
          onViewDetails={onViewDetails}
          onResetPassword={onResetPassword}
          onResendActivation={onResendActivation}
          onDeactivateUser={onDeactivateUser}
          onChangePlan={onChangePlan}
          onCancelSubscription={onCancelSubscription}
        />
      ),
    },
  ];
